import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Campaign } from '@/lib/graphql/generated/graphql';
import { formatCurrency } from '@/lib/utils';
import { storeAtom } from '@/states/store.state';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAtomValue } from 'jotai';

interface OfferSelectorProps {
  campaigns: Campaign[];
  selectedCampaignId?: string;
  onSelect: (campaign: Campaign) => void;
  loading?: boolean;
}

export function OfferSelector({
  campaigns,
  selectedCampaignId,
  onSelect,
  loading = false,
}: OfferSelectorProps) {
  const { t } = useTranslation();
  const store = useAtomValue(storeAtom);
  const selectedCampaign = campaigns.find(
    (c) => c.saleId === selectedCampaignId
  );

  const formatCampaignDate = (date: Date) => {
    return format(new Date(date), 'dd/MM/yyyy', { locale: fr });
  };

  if (campaigns.length === 0) {
    return (
      <Card className="w-full">
        <CardContent className="p-4">
          <p className="text-center text-sm text-gray-500">
            {t('components.offer_selector.no_previous_campaigns')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col justify-between">
        <span className="text-sm text-gray-500">
          {t('components.offer_selector.campaigns_available', {
            count: campaigns.length,
          })}
        </span>
      </div>

      <Select
        value={selectedCampaignId}
        onValueChange={(value) => {
          const campaign = campaigns.find((c) => c.saleId === value);
          if (campaign) {
            onSelect(campaign);
          }
        }}
        disabled={loading}
      >
        <SelectTrigger className="w-full">
          <SelectValue
            placeholder={t('components.offer_selector.select_previous_offer')}
          >
            {selectedCampaign && (
              <div className="flex w-full items-center justify-between gap-4">
                <span className="font-medium">{selectedCampaign.name}</span>
                <span className="text-sm text-gray-500">
                  {formatCampaignDate(selectedCampaign.startAt)}
                </span>
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="z-sidebar-dropdown">
          {campaigns.map((campaign) => (
            <SelectItem
              key={campaign.saleId}
              value={campaign.saleId}
              className="pr-8 [&>span:last-child]:w-full"
            >
              <div className="flex w-full flex-col gap-1 py-1">
                <div className="flex items-center justify-between gap-4">
                  <span className="font-medium">{campaign.name}</span>
                  <span className="text-sm text-gray-500">
                    {formatCampaignDate(campaign.startAt)}
                  </span>
                </div>
                <div className="flex w-full items-center justify-between gap-4 text-sm text-gray-600">
                  <span>
                    {campaign.numberOfProducts}{' '}
                    {t('components.offer_selector.variants')}
                  </span>
                  {campaign.revenue != null && (
                    <span>{formatCurrency(campaign.revenue / 100, store)}</span>
                  )}
                </div>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
